import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Paper
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const emptyState = {
  contactId: "",
  positionId: "",
  department: "",
  relationshipType: "client",
  isPrimary: false,
  active: true,
  notes: ""
};

const BusinessPartnerContactModal = ({
  open,
  onClose,
  businessPartnerId,
  onSave,
  existingRelations = [],
  editingRelation = null
}) => {
  const [record, setRecord] = useState(emptyState);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");

  useEffect(() => {
    if (!open) return;

    if (editingRelation) {
      setRecord({
        contactId: editingRelation.contactId || "",
        positionId: editingRelation.positionId || "",
        department: editingRelation.department || "",
        relationshipType: editingRelation.relationshipType || "client",
        isPrimary: !!editingRelation.isPrimary,
        active: editingRelation.active !== false,
        notes: editingRelation.notes || ""
      });
      setContactSearch(
        `${editingRelation.contact?.name || ""}${editingRelation.contact?.number ? ` - ${editingRelation.contact.number}` : ""}`
      );
    } else {
      setRecord(emptyState);
      setContactSearch("");
    }
  }, [open, editingRelation]);

  useEffect(() => {
    if (!open) return;

    const loadPositions = async () => {
      try {
        const positionsRes = await api.get("/positions");
        setPositions((Array.isArray(positionsRes?.data?.positions) ? positionsRes.data.positions : []).filter(item => item && typeof item === "object" && item.id != null));
      } catch (err) {
        toastError(err);
      }
    };

    loadPositions();
  }, [open]);

  useEffect(() => {
    if (!open || editingRelation) return;

    const loadContacts = async () => {
      try {
        const contactsRes = await api.get("/contacts/search/lite", {
          params: {
            searchParam: contactSearch || ""
          }
        });

        setContacts((Array.isArray(contactsRes?.data?.contacts) ? contactsRes.data.contacts : []).filter(item => item && typeof item === "object" && item.id != null));
      } catch (err) {
        toastError(err);
      }
    };

    const timer = setTimeout(() => {
      loadContacts();
    }, 250);

    return () => clearTimeout(timer);
  }, [open, contactSearch, editingRelation]);

	const linkedContactIds = useMemo(
	  () =>
	    (Array.isArray(existingRelations) ? existingRelations : [])
	      .filter(Boolean)
	      .filter(item => !editingRelation || Number(item.id) !== Number(editingRelation.id))
	      .map(item => Number(item.contactId)),
	  [existingRelations, editingRelation]
	);

  const availableContacts = useMemo(
    () =>
      (Array.isArray(contacts) ? contacts : [])
        .filter(item => item && typeof item === "object" && item.id != null)
        .filter(item => {
          const id = Number(item.id);
          if (editingRelation && Number(editingRelation.contactId) === id) return true;
          return !linkedContactIds.includes(id);
        }),
    [contacts, linkedContactIds, editingRelation]
  );

  const selectedContact = useMemo(
    () => (Array.isArray(availableContacts) ? availableContacts : []).find(item => item && Number(item.id) === Number(record.contactId)) || null,
    [availableContacts, record.contactId]
  );

  const handleChange = e => {
    const { name, value } = e.target;
    setRecord(prev => ({ ...prev, [name]: value }));
  };

  const handleToggle = e => {
    const { name, checked } = e.target;
    setRecord(prev => ({ ...prev, [name]: checked }));
  };

  const handleSave = async () => {
    try {
      if (!record.contactId) {
        toast.error("Selecione um contato");
        return;
      }

      if (!record.relationshipType || !record.relationshipType.trim()) {
        toast.error("Tipo de relação é obrigatório");
        return;
      }

      if (
        linkedContactIds.includes(Number(record.contactId)) &&
        (!editingRelation || Number(editingRelation.contactId) !== Number(record.contactId))
      ) {
        toast.error("Este contato já está vinculado a este Parceiro de Negócios");
        return;
      }

      setLoading(true);

      const payload = {
        contactId: Number(record.contactId),
        businessPartnerId: Number(businessPartnerId),
        positionId: record.positionId ? Number(record.positionId) : null,
        department: record.department,
        relationshipType: record.relationshipType,
        isPrimary: record.isPrimary,
        active: record.active,
        notes: record.notes
      };

      if (editingRelation) {
        await api.put(`/contact-business-partners/${editingRelation.id}`, payload);
        toast.success("Vínculo atualizado com sucesso");
      } else {
        await api.post("/contact-business-partners", payload);
        toast.success("Contato vinculado com sucesso");
      }

      setLoading(false);
      onSave && onSave();
      onClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editingRelation ? "Editar Vínculo" : "Vincular Contato"}</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Pesquisar contato por nome ou número"
              value={contactSearch}
              onChange={e => {
                setContactSearch(e.target.value);
                setRecord(prev => ({ ...prev, contactId: "" }));
              }}
              disabled={loading || !!editingRelation}
            />
          </Grid>

          {!editingRelation && (
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ maxHeight: 220, overflowY: "auto", padding: 8 }}>
                {availableContacts.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhum contato disponível para vínculo com o filtro atual.
                  </Typography>
                ) : (
                  availableContacts.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                    <div
                      key={item.id}
                      onClick={() => {
                        setRecord(prev => ({ ...prev, contactId: item.id }));
                        setContactSearch(`${item.name}${item.number ? ` - ${item.number}` : ""}`);
                      }}
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        cursor: "pointer",
                        backgroundColor: Number(record.contactId) === Number(item.id) ? "rgba(0,0,0,0.08)" : "transparent",
                        marginBottom: 4
                      }}
                    >
                      <Typography variant="body2">{item.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.number || ""} {item.email ? `• ${item.email}` : ""}
                      </Typography>
                    </div>
                  ))
                )}
              </Paper>
            </Grid>
          )}

          {selectedContact && !editingRelation && (
            <Grid item xs={12}>
              <Typography variant="body2" color="primary">
                Selecionado: {selectedContact.name} {selectedContact.number ? `- ${selectedContact.number}` : ""}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Posição</InputLabel>
              <Select
                name="positionId"
                value={record.positionId}
                onChange={handleChange}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Nenhuma</em>
                </MenuItem>
                {positions.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Departamento"
              name="department"
              value={record.department}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Relação</InputLabel>
              <Select
                name="relationshipType"
                value={record.relationshipType}
                onChange={handleChange}
                disabled={loading}
              >
                <MenuItem value="client">Cliente</MenuItem>
                <MenuItem value="agency">Agência</MenuItem>
                <MenuItem value="partner">Parceiro</MenuItem>
                <MenuItem value="supplier">Fornecedor</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Observações"
              name="notes"
              value={record.notes}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={record.isPrimary}
                  onChange={handleToggle}
                  name="isPrimary"
                  color="primary"
                  disabled={loading}
                />
              }
              label="Contato principal"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={record.active}
                  onChange={handleToggle}
                  name="active"
                  color="primary"
                  disabled={loading}
                />
              }
              label="Ativo"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={loading}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessPartnerContactModal;
